import { FormEvent, useEffect, useState } from 'react';

import { JDSSelectorProps } from './generated';
import { useRootRef } from '../../hooks/useRootRef';

export interface SelectorItemProps {
  value: string;
  label: string;
  icon?: JSX.Element;
  iconOnly?: boolean;
  id?: string;
  defaultChecked?: boolean;
  check?: boolean;
}

export interface SelectorsInputProps {
  /**
   * Selector is radio button behind the scenes. This acts as the `name` attribute
   * for all radio button.
   */
  name?: string;
  /**
   * Current selected item. You can overwrite it by passing a new value
   */
  selectedIndex?: number;
}

export interface SelectorProps
  extends SelectorsInputProps,
    Omit<JDSSelectorProps, 'selectedIndex' | 'items' | 'onClick' | 'kind'> {
  /**
   * An array of each item for the selector. Accepts as an array of object with type <br>
   * `{ value: string; label: string; icon?: JSX.Element; iconOnly?: boolean;`<br>
   * `defaultChecked?: boolean; ...otherInputProps }[]`<br>
   * `'defaultChecked?: boolean; has been DEPRECATED`<br>
   */
  items: Array<SelectorItemProps>;
  /**
   * An overwritten `onChange` function that passes the `value` of the selected
   * item as well as its `index`.
   * @param value
   * @param event
   */
  onChange?: (
    value: string,
    index: number,
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

export const withLogic =
  (Component: React.ComponentType<SelectorProps>): React.FC<SelectorProps> =>
  ({ items: userItems = [], ...props }: SelectorProps) => {
    const [selectedIndex, setSelectedIndex] = useState(
      props.selectedIndex || 0
    );
    const [dimensions, setDimensions] = useState({
      left: 0,
      width: 0,
      height: 0
    });
    const { rootRef } = useRootRef(
      (element) => {
        const selectorContainers = Array.from(
          element?.querySelectorAll('div.j-JDSSelectorItem-container') || []
        );
        const selectedContainer = selectorContainers[
          selectedIndex
        ] as HTMLElement;

        setDimensions({
          left: selectedContainer?.offsetLeft,
          width: selectedContainer?.offsetWidth,
          height: selectedContainer?.offsetHeight
        });
      },
      [selectedIndex, props.stretch, props.wrap]
    );

    useEffect(() => {
      if (typeof props.selectedIndex === 'number')
        setSelectedIndex(props.selectedIndex);
      else setSelectedIndex(0);
    }, [props.selectedIndex]);

    const items = userItems.map((item, i) => ({
      ...item,
      selected: selectedIndex === i,
      selectedIndex,
      onClick: (ev: React.MouseEvent<HTMLInputElement>) => {
        setSelectedIndex(i);
        props.onChange?.(item.value, i, ev);
      }
    }));
    return (
      <Component
        {...{
          ...props,
          selectedIndex,
          items,
          rootRef,
          _left: dimensions.left,
          _width: dimensions.width,
          _height: dimensions.height
        }}
      />
    );
  };
